import React, { ReactNode } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage, IntlShape } from 'react-intl';

import { CoreCommonMessages, ReactTableEnums, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { CountryCode, PageName } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { gameLinksColumn, networkColumn } from '~/core/tableColumns/scheduleTableColumns/columns';
import { getGameLinks } from '~/core/tableColumns/scheduleTableColumns/utils';
import { GameLinksPopperWrapper } from '~/views/components/gameLinksPopper/GameLinksPopper';

import { PlayoffSeriesGame, PlayoffSeriesTeam } from '../../types';

import { gameNumberAndDateColumnStyle, matchupColumnStyle } from './columnStyles';
import TeamMatchup from './components/TeamMatchup/TeamMatchup';
import { getGameNumberLabel } from './utils';

import { GameDate, GameNumber, GameNumberDateContainer, SIZES } from './styles';

const columnHelper = createColumnHelper<PlayoffSeriesGame>();

const getPlayoffSeriesColumns = (
    intl: IntlShape,
    bottomSeedTeam: PlayoffSeriesTeam,
    topSeedTeam: PlayoffSeriesTeam,
    countryCode: CountryCode,
    neededToWin: number,
    isMediumScreenOrSmaller: boolean
): Array<ColumnDef<PlayoffSeriesGame>> => [
    columnHelper.display({
        id: 'gameNumberAndDate',
        cell: function CellComponent({ row }) {
            const { gameNumber, gameNumberOfSeries, ifNecessary, startTimeUTC } = row.original;
            const gameCount = gameNumber || gameNumberOfSeries || null;
            const isWebview = useIsWebview();

            const ticketsLink = useOptionForLocale({
                [ShortCodeEnums.Language.English]: row.original.ticketsLink,
                [ShortCodeEnums.Language.French]: row.original.ticketsLinkFr,
            });

            const gameLinks = getGameLinks({
                game: row.original,
                showOnlyHomeTickets: false,
                pageName: PageName.PlayoffSeries,
                ticketsLink,
                isWebview,
            });

            const gameNumberDescription = getGameNumberLabel(intl, gameCount, ifNecessary);
            const getGameNumberAndDateContainer = (): ReactNode => (
                <GameNumberDateContainer aria-label={gameNumberDescription}>
                    <GameNumber>{gameNumberDescription}</GameNumber>
                    {isMediumScreenOrSmaller && <>&nbsp;•&nbsp;</>}
                    <GameDate>
                        {startTimeUTC === undefined ? (
                            <FormattedMessage
                                {...CoreCommonMessages.gameScheduleStateDescriptions.abbreviations[
                                    ShortCodeEnums.GameScheduleState.toBeDetermined
                                ]}
                            />
                        ) : (
                            DateTime.fromISO(startTimeUTC).toLocaleString({
                                month: 'short',
                                weekday: 'short',
                                day: 'numeric',
                            })
                        )}
                    </GameDate>
                </GameNumberDateContainer>
            );

            // Only render the popper icon if there are actual game links, otherwise just show game date and number info
            return gameLinks.length ? (
                <GameLinksPopperWrapper game={row.original} gameLinks={gameLinks} isPlayoffSeriesPage>
                    {getGameNumberAndDateContainer()}
                </GameLinksPopperWrapper>
            ) : (
                getGameNumberAndDateContainer()
            );
        },
        size: SIZES.gameNumberAndDate.size,
        meta: {
            className: 'playoffSeriesColumn',
            scope: ReactTableEnums.Scopes.ROW,
            style: gameNumberAndDateColumnStyle,
        },
    }),
    columnHelper.display({
        id: 'teamMatchup',
        cell: ({ row }) => <TeamMatchup {...row.original} bottomSeedTeam={bottomSeedTeam} topSeedTeam={topSeedTeam} />,
        size: isMediumScreenOrSmaller ? SIZES.teamMatchup.smallSize : SIZES.teamMatchup.size,
        meta: {
            className: 'playoffSeriesColumn',
            style: matchupColumnStyle,
        },
    }),
    networkColumn(
        countryCode,
        false,
        PageName.PlayoffSeries,
        bottomSeedTeam,
        topSeedTeam,
        neededToWin
    ) as ColumnDef<PlayoffSeriesGame>,
    gameLinksColumn({
        intl,
        showOnlyHomeTickets: false,
        countryCode,
        pageName: PageName.PlayoffSeries,
    }) as ColumnDef<PlayoffSeriesGame>,
];

export default getPlayoffSeriesColumns;
